/**
 * A página do recurso (spec 032, §7) — para o veículo do SGR, a "tela do
 * cliente final": placa, marca e modelo, cor, ano, chassi, situação na origem,
 * o associado (link), o cliente e "atualizado há X"; e os tickets do veículo em
 * TODAS as mesas — ligados ao recurso OU pela placa (o importado aparece sem
 * ser escrito, D7), com a visibilidade da busca.
 *
 * Os campos do SGR são só leitura. O GET nunca escreve: passadas 24 h da última
 * leitura, a página mostra o que está guardado e pede `POST …/sgr-refresh`
 * sozinha; o botão "Atualizar" é o mesmo pedido (1 por minuto).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { getRouteApi, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Pencil, RefreshCw } from 'lucide-react';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import { useTenantProfile } from '@/shared/tenant-profile';
import { equipmentDetailOptions, errorMessage } from '@/features/resources/resources-queries';
import { ResourceDrawer } from '@/features/resources/resource-drawer';
import { DeskSection } from '@/features/tickets/TicketSearchPage';
import { useSgr, useSgrPageRefresh, vehicleTicketsQuery } from './sgr-queries';
import { capitalize, pageRefreshText, updatedAgoText } from './sgr-view';

const routeApi = getRouteApi('/_authenticated/resources/$resourceId');

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm break-words">{children ?? <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

export function VehiclePage() {
  const { resourceId } = routeApi.useParams();
  const id = Number(resourceId);
  const profile = useTenantProfile();
  const terms = profile.terms;
  const { can } = usePermissions();
  const sgr = useSgr();
  const detailQ = useQuery(equipmentDetailOptions(id));
  const ticketsQ = useQuery(vehicleTicketsQuery('by-resource', id));
  const refresh = useSgrPageRefresh('resources', id);
  const [notice, setNotice] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const autoRefreshed = useRef(false);
  const eq = detailQ.data;
  const block = eq?.sgr ?? null;

  // passou de 24 h: mostra o guardado e pede a leitura nova (uma vez por visita)
  useEffect(() => {
    if (!block?.stale || autoRefreshed.current || !sgr.available) return;
    autoRefreshed.current = true;
    refresh.mutate(undefined, { onSuccess: (res) => (res.status === 'ok' ? null : setNotice(pageRefreshText(res, sgr.sourceName))) });
  }, [block?.stale, sgr.available]); // eslint-disable-line react-hooks/exhaustive-deps

  if (detailQ.isPending) {
    return (
      <div className="space-y-3" aria-busy>
        <Skeleton className="h-6 w-60" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (detailQ.isError || !eq) {
    return <EmptyState className="h-40" message={`Erro ao carregar: ${errorMessage(detailQ.error)}`} />;
  }

  const vehicleTitle = block ? `${capitalize(terms.resource)} ${eq.name}` : eq.name;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[13px] text-muted-foreground">
            <Link to="/resources" className="hover:underline">
              {capitalize(terms.resources)}
            </Link>
          </p>
          <h1 className="text-xl font-semibold">{vehicleTitle}</h1>
          {block ? (
            <p className="text-sm text-muted-foreground">
              {[block.brand, block.model].filter(Boolean).join(' ') || '—'}
              <span className="ml-2 text-xs">
                {`${updatedAgoText(block.syncedAt)} do ${sgr.sourceName || 'cadastro de origem'}`}
              </span>
            </p>
          ) : null}
        </div>
        <div className="flex gap-2">
          {block && sgr.available ? (
            <Button
              size="sm"
              variant="outline"
              disabled={refresh.isPending}
              onClick={() => refresh.mutate(undefined, { onSuccess: (res) => setNotice(pageRefreshText(res, sgr.sourceName)) })}
            >
              {refresh.isPending ? <Spinner className="size-4" /> : <RefreshCw className="size-3.5" />}
              {`Atualizar do ${sgr.sourceName}`}
            </Button>
          ) : null}
          {can('resources.manage') ? (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil className="size-3.5" />
              Editar
            </Button>
          ) : null}
        </div>
      </div>

      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      {block?.missingSince ? (
        <p role="alert" className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-700 dark:bg-amber-950/40">
          <AlertTriangle className="size-4 text-amber-600" aria-hidden />
          {`Não encontrado no ${sgr.sourceName || 'cadastro de origem'} desde ${formatDateTime(block.missingSince)} — nada foi apagado aqui.`}
        </p>
      ) : null}

      <section className="rounded-lg border bg-card p-4 shadow-xs">
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          {block ? (
            <>
              <Field label={capitalize(terms.resourceId)}>
                <span className="font-semibold tabular-nums">{eq.name}</span>
              </Field>
              <Field label="Marca e modelo">{[block.brand, block.model].filter(Boolean).join(' ') || null}</Field>
              <Field label="Cor">{block.color}</Field>
              <Field label="Ano">{block.year}</Field>
              <Field label="Chassi">{block.chassis ? <span className="tabular-nums">{block.chassis}</span> : null}</Field>
              <Field label={`Situação no ${sgr.sourceName || 'cadastro de origem'}`}>
                {block.status ? (
                  <span className={block.inactive ? 'text-amber-700 dark:text-amber-300' : undefined}>
                    {block.status}
                    {block.inactive ? ' — inativo na origem; nada é bloqueado aqui' : ''}
                  </span>
                ) : null}
              </Field>
              <Field label={capitalize(terms.contact)}>
                {block.endCustomer ? (
                  <Link
                    to="/end-customers/$endCustomerId"
                    params={{ endCustomerId: String(block.endCustomer.id) }}
                    className="font-medium text-primary hover:underline"
                  >
                    {block.endCustomer.name}
                  </Link>
                ) : null}
              </Field>
              <Field label={capitalize(terms.client)}>
                {eq.clientName ?? (
                  <span className="text-muted-foreground">
                    {`sem ${terms.client} — o ponto de venda ${block.salePoint?.name ?? ''} ainda não foi ligado`}
                  </span>
                )}
              </Field>
              <Field label="Código do cadastro">{block.sgrId}</Field>
            </>
          ) : (
            <>
              <Field label={capitalize(terms.client)}>{eq.clientName}</Field>
              <Field label="Tipo">{eq.equipmentTypeName}</Field>
              <Field label="Serial">{eq.serialNumber}</Field>
            </>
          )}
          <div className="sm:col-span-3">
            <Field label="Observações">{eq.notes ? <span className="whitespace-pre-wrap">{eq.notes}</span> : null}</Field>
          </div>
        </div>
      </section>

      <section aria-label={`${capitalize(terms.tickets)} do ${terms.resource}`} className="space-y-2">
        <h2 className="text-base font-semibold">{`${capitalize(terms.tickets)} do ${terms.resource}`}</h2>
        {ticketsQ.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : ticketsQ.isError ? (
          <EmptyState className="h-24" message={`Erro ao carregar: ${errorMessage(ticketsQ.error)}`} />
        ) : ticketsQ.data.groups.length === 0 ? (
          <EmptyState className="h-24" message={`Nenhum ${terms.ticket} deste ${terms.resource}.`} />
        ) : (
          <div className="flex flex-col">
            {ticketsQ.data.groups.map((g) => (
              <DeskSection key={g.desk.id} group={g} />
            ))}
          </div>
        )}
      </section>

      {editOpen ? <ResourceDrawer initial={eq} onClose={() => setEditOpen(false)} /> : null}
    </div>
  );
}
